import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, AlertCircle, Plug, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Register = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [bybitId, setBybitId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [loading, setLoading] = useState(false);
  const [waitingApproval, setWaitingApproval] = useState(false);
  const [token, setToken] = useState(null);

  // Check approval status periodically
  const checkApproval = useCallback(async () => {
    if (!token) return;
    
    try {
      const res = await axios.get(`${API}/auth/check-approval`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.is_approved) {
        // User approved! Login and redirect
        const meRes = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Store token and user data
        localStorage.setItem("token", token);
        localStorage.setItem("user", JSON.stringify(meRes.data));
        
        toast.success("Аккаунт активирован! Добро пожаловать!");
        window.location.href = "/dashboard";
      }
    } catch (err) {
      console.error("Error checking approval:", err);
    }
  }, [token]);

  useEffect(() => {
    let interval;
    if (waitingApproval && token) {
      // Check every 3 seconds
      interval = setInterval(checkApproval, 3000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [waitingApproval, token, checkApproval]);

  // Validate international phone number
  const validatePhone = (phoneNumber) => {
    const cleaned = phoneNumber.replace(/[^\d+]/g, '');
    const digitsOnly = cleaned.replace(/\D/g, '');
    
    if (digitsOnly.length < 7) {
      return { valid: false, message: "Слишком мало цифр. Введите полный номер телефона" };
    }
    if (digitsOnly.length > 15) {
      return { valid: false, message: "Слишком много цифр. Проверьте номер телефона" };
    }
    return { valid: true };
  };

  // Format phone number as user types
  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Allow only digits, +, spaces, parentheses, and dashes
    const filtered = value.replace(/[^\d+\s()\-]/g, '');
    setPhone(filtered);
    setPhoneError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setPhoneError("");
    setLoading(true);

    if (password.length < 6) {
      setError("Пароль должен быть минимум 6 символов");
      setLoading(false);
      return;
    }

    if (!phone.trim()) {
      setPhoneError("Введите номер телефона");
      setLoading(false);
      return;
    }

    // Validate phone
    const phoneValidation = validatePhone(phone);
    if (!phoneValidation.valid) {
      setPhoneError(phoneValidation.message);
      setLoading(false);
      return;
    }

    try {
      const res = await axios.post(`${API}/auth/register`, {
        email,
        password,
        name,
        phone,
        bybit_id: bybitId,
        api_key: apiKey
      });
      
      setToken(res.data.token);
      setWaitingApproval(true);
      toast.success("Регистрация отправлена на проверку");
    } catch (err) {
      const message = err.response?.data?.detail || "Ошибка регистрации";
      setError(message);
      toast.error(message);
      setLoading(false);
    }
  };

  // Waiting for approval screen
  if (waitingApproval) {
    return (
      <div className="min-h-screen bg-[#000000] flex flex-col items-center justify-center px-4">
        <div className="text-center max-w-md">
          {/* W3 Logo */}
          <div className="mb-8">
            <div className="text-5xl font-bold text-white mx-auto">
              Web-<span className="text-[#EDEDED]">3</span>
            </div>
          </div>

          {/* Connection Icon with Animation */}
          <div className="relative mb-8">
            <div className="w-20 h-20 mx-auto bg-white/20 rounded-full flex items-center justify-center">
              <Plug className="w-10 h-10 text-[#EDEDED] animate-pulse" />
            </div>
            <div className="absolute inset-0 w-20 h-20 mx-auto border-2 border-white/30 rounded-full animate-ping"></div>
          </div>

          {/* Loading Spinner */}
          <div className="mb-6">
            <Loader2 className="w-8 h-8 mx-auto text-[#EDEDED] animate-spin" />
          </div>

          {/* Text */}
          <h2 className="font-chivo font-bold text-2xl text-white mb-4">
            Подождите...
          </h2>
          <p className="text-[#A1A1AA] text-lg mb-2">
            Идёт подключение к платформе
          </p>
          <p className="text-[#71717A] text-sm">
            Это может занять несколько минут.<br/>
            Пожалуйста, не закрывайте страницу.
          </p>

          {/* Progress dots animation */}
          <div className="flex justify-center gap-2 mt-8">
            <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></div>
            <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></div>
            <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bybit-grid-bg flex flex-col">
      {/* Header */}
      <header className="border-b border-[#1F1F1F] bg-[#000000]/90 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <h1 className="font-chivo font-bold text-xl text-white">Web-<span className="text-[#EDEDED]">3</span></h1>
            </Link>
            <Link 
              to="/login" 
              className="btn-primary"
              data-testid="header-login-btn"
            >
              Войти
            </Link>
          </div>
        </div>
      </header>

      {/* Register Form */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="bg-[#0A0A0C] rounded-lg border border-[#1F1F1F] p-8">
            <h2 className="font-chivo font-bold text-2xl text-white text-center mb-8">Регистрация</h2>
            
            {error && (
              <div className="bg-[#F87171]/10 border border-[#F87171]/50 rounded-lg p-4 mb-6 flex items-center gap-3">
                <AlertCircle className="text-[#F87171] flex-shrink-0" size={20} />
                <span className="text-[#F87171] text-sm">{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Укажите имя и фамилию"
                  className="w-full bg-[#000000] border border-[#1F1F1F] rounded px-4 py-3 text-white placeholder-[#A1A1AA] focus:border-white focus:outline-none transition-colors"
                  required
                  data-testid="register-name-input"
                />
              </div>
              <div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  className="w-full bg-[#000000] border border-[#1F1F1F] rounded px-4 py-3 text-white placeholder-[#A1A1AA] focus:border-white focus:outline-none transition-colors"
                  required
                  data-testid="register-email-input"
                />
              </div>
              <div>
                <input
                  type="tel"
                  value={phone}
                  onChange={handlePhoneChange}
                  placeholder="Номер телефона: +7 (926) 123-45-67"
                  className={`w-full bg-[#000000] border rounded px-4 py-3 text-white placeholder-[#A1A1AA] focus:outline-none transition-colors ${
                    phoneError ? 'border-[#F87171] focus:border-[#F87171]' : 'border-[#1F1F1F] focus:border-white'
                  }`}
                  required
                  data-testid="register-phone-input"
                />
                {phoneError && (
                  <p className="text-[#F87171] text-sm mt-1 flex items-center gap-1">
                    <AlertCircle size={14} />
                    {phoneError}
                  </p>
                )}
              </div>
              <div>
                <input
                  type="text"
                  value={bybitId}
                  onChange={(e) => setBybitId(e.target.value)}
                  placeholder="UID Connect"
                  className="w-full bg-[#000000] border border-[#1F1F1F] rounded px-4 py-3 text-white placeholder-[#A1A1AA] focus:border-[#FF8A1E] focus:outline-none transition-colors"
                  data-testid="register-bybitid-input"
                />
              </div>
              <div>
                <input
                  type="text"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="API Key"
                  className="w-full bg-[#000000] border border-[#1F1F1F] rounded px-4 py-3 text-white placeholder-[#A1A1AA] focus:border-[#FF8A1E] focus:outline-none transition-colors"
                  data-testid="register-apikey-input"
                />
              </div>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Пароль (минимум 6 символов)"
                  className="w-full bg-[#000000] border border-[#1F1F1F] rounded px-4 py-3 text-white placeholder-[#A1A1AA] focus:border-white focus:outline-none transition-colors pr-12"
                  required
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <button
                type="submit"
                disabled={loading || !!phoneError}
                className={`w-full py-3 font-bold rounded transition-all duration-200 ${
                  name && email && phone && password && password.length >= 6 && !phoneError
                    ? 'bg-[#FF8A1E] hover:bg-[#E67A00] text-white cursor-pointer' 
                    : 'bg-[#1F1F1F] text-[#71717A] cursor-not-allowed'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
                data-testid="register-submit-btn"
              >
                {loading ? "Регистрация..." : "Зарегистрироваться"}
              </button>
            </form>

            <p className="text-center mt-6 text-[#A1A1AA]">
              Уже есть аккаунт?{" "}
              <Link to="/login" className="text-[#EDEDED] hover:underline" data-testid="login-link">
                Войдите
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
